import { axiosInstance } from "../axiosInstance";
import { 
  Transaction, 
  TransactionFilters, 
  CreateTransactionRequest, 
  UpdateTransactionRequest 
} from "./types";

interface TransactionsResponse {
  data: Transaction[];
  page: number;
  limit: number;
  total: number;
}

export const partnerTransactionsApi = {
  // Fetch all transactions with filters
  fetchTransactions: async (filters?: TransactionFilters): Promise<TransactionsResponse> => {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.searchTerm) params.append('search', filters.searchTerm);
        if (filters.types?.length) params.append('types', filters.types.join(','));
        if (filters.entityTypes?.length) params.append('entityTypes', filters.entityTypes.join(','));
        if (filters.quarter) params.append('quarter', filters.quarter.toString());
        if (filters.year) params.append('year', filters.year.toString());
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.partnerId) params.append('partnerId', filters.partnerId);
      }
      
      const response = await axiosInstance.get(`/api/transactions?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch transactions');
    }
  },

  // Fetch transactions for a specific partner
  fetchPartnerTransactions: async (partnerId: string, filters?: TransactionFilters): Promise<TransactionsResponse> => {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.searchTerm) params.append('search', filters.searchTerm);
        if (filters.types?.length) params.append('types', filters.types.join(','));
        if (filters.quarter) params.append('quarter', filters.quarter.toString());
        if (filters.year) params.append('year', filters.year.toString());
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
      }
      
      const response = await axiosInstance.get(`/api/partners/${partnerId}/transactions?${params.toString()}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch partner transactions');
    }
  },

  // Fetch single transaction
  fetchTransaction: async (transactionId: string): Promise<Transaction> => {
    try {
      const response = await axiosInstance.get(`/api/transactions/${transactionId}`);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch transaction');
    }
  },

  // Create new transaction
  createTransaction: async (data: CreateTransactionRequest): Promise<Transaction> => {
    try {
      const formData = new FormData();
      
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'attachment' && value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, value.toString());
          }
        }
      });
      
      const response = await axiosInstance.post('/api/transactions', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to create transaction');
    }
  },

  // Update transaction
  updateTransaction: async (transactionId: string, data: UpdateTransactionRequest): Promise<Transaction> => {
    try {
      const formData = new FormData();
      
      Object.entries(data).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'attachment' && value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, value.toString());
          }
        }
      });
      
      const response = await axiosInstance.put(`/api/transactions/${transactionId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update transaction');
    }
  },

  // Delete transaction
  deleteTransaction: async (transactionId: string): Promise<void> => {
    try {
      await axiosInstance.delete(`/api/transactions/${transactionId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to delete transaction');
    }
  },

  // Export transactions to CSV
  exportTransactions: async (filters?: TransactionFilters): Promise<Blob> => {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.searchTerm) params.append('search', filters.searchTerm);
        if (filters.types?.length) params.append('types', filters.types.join(','));
        if (filters.entityTypes?.length) params.append('entityTypes', filters.entityTypes.join(','));
        if (filters.quarter) params.append('quarter', filters.quarter.toString());
        if (filters.year) params.append('year', filters.year.toString());
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.partnerId) params.append('partnerId', filters.partnerId);
      }
      
      const response = await axiosInstance.get(`/api/transactions/export?${params.toString()}`, {
        responseType: 'blob',
      });
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to export transactions');
    }
  },

  // Download transaction attachment
  downloadAttachment: async (transactionId: string): Promise<Blob> => {
    try {
      const response = await axiosInstance.get(`/api/transactions/${transactionId}/attachment`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to download attachment');
    }
  },

  // Update transaction status (for real-time updates)
  updateTransactionStatus: async (transactionId: string, status: string): Promise<Transaction> => {
    try {
      const response = await axiosInstance.patch(`/api/transactions/${transactionId}/status`, { status });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to update transaction status');
    }
  },
};